//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { CampaignRequestBase } from '@apis/campaign-requests/shared/CampaignRequests';
import { SortDir } from '@apis/campaign-requests/shared/Sorting';
import { BazaarvoiceClientId } from '@models/BazaarvoiceClientId';
import { ExternalQuoteId } from '@models/ExternalQuoteId';
import { GuidanceId } from '@models/GuidanceId';
import { QuoteId } from '@models/QuoteId';
import { SamplingCampaignRequestId } from '@models/SamplingCampaignRequestId';
import { Serialized } from '@utils/types/Serialized';

export interface AgeRange {
  min: number;
  max: number;
}

export type Gender = 'male' | 'female' | 'non-binary' | 'everyone';

export interface AudienceTargetingSections {
  children?: string;
  country?: string;
  exclude?: string;
  extraRequirements?: string;
  household?: string;
  installation?: string;
  location?: string;
  pets?: string;
}

export interface AmbassadorTargeting {
  vibeCommunityId: number;
  fallback: boolean;
}

export interface AudienceDetails extends AudienceTargetingSections {
  age?: AgeRange;
  gender?: Gender[];
  country?: SamplingCampaignRequestCountry;
  ambassadorTargeting?: AmbassadorTargeting;
}

export interface RetailerPdpUrl {
  productId: string;
  pdpUrl: string;
}

export interface Retailer {
  locatorLink?: string;
  pdpUrls?: RetailerPdpUrl[];
  redemptionGuidance?: string;
  retailerName?: string;
}

export interface Deals {
  codeScope: 'perUnit' | 'perProduct';
  products: {
    productId: string;
    quantity: number;
  }[];
  retailers: string[];
}

export const BOX_TYPE_PLAIN = 'plain';
export const BOX_TYPE_BRANDED = 'branded';
export const BOX_TYPE_CLIENT_PROVIDED = 'client-provided';

export type BoxType =
  | typeof BOX_TYPE_PLAIN
  | typeof BOX_TYPE_BRANDED
  | typeof BOX_TYPE_CLIENT_PROVIDED;

export const PACKAGE_INSERT_NONE = 'none';
export const PACKAGE_INSERT_CLIENT_PROVIDED = 'client-provided';

export type PackageInsertType =
  | typeof PACKAGE_INSERT_NONE
  | typeof PACKAGE_INSERT_CLIENT_PROVIDED;

export type PackageWeightType = 'metric' | 'imperial';

export type RiskManagement =
  | 'acceptingRecommendation'
  | 'decliningRecommendation';

export type SendTo =
  | 'sendToMe'
  | 'sendToOther'
  /**
   * @deprecated This option is being phased out. Use 'sendToMe' or 'sendToOther' instead.
   * Retained for compatibility with existing data.
   */
  | 'sendToMeAndOthers';

export type RedemptionType = 'instore' | 'online' | 'instorePickup';

export type CodeScope = 'perUnit' | 'perProduct' | 'perCampaign';

export type CodeType = 'percentageDiscount' | 'setAmount';

export type WhoShipsProduct = 'bazaarvoice' | 'myCompany';

export type FulfillmentType = 'shipped' | 'deals';

export type CodeSupplier = 'client' | 'bazaarVoice';

export interface ClientCode {
  productId: string;
  retailers: string;
  codes?: string[];
}

export interface FulfillmentDetails {
  boxType?: BoxType;
  codeScope?: CodeScope;
  clientCodes?: ClientCode[];
  clientCodesSupplied?: boolean;
  codeSupplier?: CodeSupplier;
  codeType?: CodeType;
  codeValue?: number;
  emails?: string[];
  fulfillmentType?: FulfillmentType;
  packageInsert?: PackageInsertType;
  packageWeight?: PackageWeight;
  participantCount?: number;
  redemptionType?: RedemptionType[];
  retailers?: Retailer[];
  riskManagement?: RiskManagement;
  sendTo?: SendTo;
  whoShipsProduct?: WhoShipsProduct;
}

export interface PackageWeight {
  kg?: number;
  lb?: number;
  oz?: number;
  unit?: PackageWeightType;
}

export interface Comments {
  audience?: string;
  basics?: string;
  products?: string;
  shipping?: string;
  submission?: string;
  summary?: string;
  tasks?: string;
  packaging?: string;
}

export type Platform = 'instagramPost' | 'instagramStory' | 'tiktok';

export interface Social {
  handle?: string;
  hashtag?: string;
  platform: Platform;
}

export interface MemberTasks {
  postOnSocial?: boolean;
  socials?: Social[];
  writeReview?: boolean;
  postSurvey?: boolean;
}

export enum CampaignTask {
  REVIEW = 'REVIEW',
  SOCIAL = 'SOCIAL',
  POST_SURVEY = 'POST_SURVEY',
}

export interface CampaignRequestGuidanceItem {
  guidanceId: GuidanceId;
  imageUrl?: string;
}

export interface DisplayReviews {
  mySite?: boolean;
  otherSites?: boolean;
  // TODO: PD-246036 - Review if null is a valid value and if it's different from undefined
  retailPartnerSites?: string;
}

export type SamplingCampaignRequestStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'quote';

export interface SamplingCampaignRequestApiQuery {
  formId?: string;
  formOwner?: string | string[];
  lastUpdatedFrom?: Date;
  lastUpdatedTo?: Date;
  limit?: number;
  offset?: number;
  search?: string;
  sortby?: string;
  sortorder?: string;
  status?: string | string[];
}

export interface SamplingCampaignRequestReduced
  extends Pick<
    SamplingCampaignRequest,
    | 'campaignRequestId'
    | 'externalId'
    | 'formOwner'
    | 'lastUpdated'
    | 'name'
    | 'status'
  > {
  numberOfUnits?: number;
  numberOfProducts?: number;
}

export enum SamplingCampaignRequestsSortKey {
  LAST_UPDATED = 'lastUpdated',
  FORM_OWNER = 'formOwner',
}

export interface GetClientSamplingCampaignRequestsRequest {
  limit?: number;
  offset?: number;
  searchTerm?: string;
  sortCriteria?: SamplingCampaignRequestsSortKey;
  sortDirection?: SortDir;
  status?: SamplingCampaignRequestStatus[];
}

export interface GetSamplingCampaignRequestsResponse {
  data: SamplingCampaignRequestsResponse;
}

export interface SamplingCampaignRequestsResponse {
  campaignRequests: SamplingCampaignRequestReduced[];
  offset: number;
  limit: number;
  total: number;
}

export interface GetClientSamplingCampaignRequestByIdRequest {
  campaignId?: SamplingCampaignRequestId;
}

export interface GetSamplingCampaignRequestResponse {
  data: SamplingCampaignRequest;
}

// TODO: PD-244879 - Check if we need to add more locales
export type SamplingCampaignRequestLocale =
  | 'en_US'
  | 'en_GB'
  | 'fr_FR'
  | 'de_DE'
  | 'es_ES'
  | 'nl_NL'
  | 'en_CA'
  | 'it_IT'
  | 'no_NO'
  | 'sv_SE'
  | 'da_DK'
  | 'fi_FI'
  | 'pl_PL'
  | 'cs_CZ'
  | 'hu_HU'
  | 'de_AT'
  | 'nl_BE'
  | 'fr_BE'
  | 'de_BE'
  | 'fr_LU'
  | 'de_LU'
  | 'fr_CH'
  | 'de_CH'
  | 'en_AU'
  | 'fr_CA'
  | 'pt_PT';

export type SamplingCampaignRequestCountry =
  | 'US' // United States
  | 'GB' // United Kingdom
  | 'CA' // Canada
  | 'FR' // France
  | 'ES' // Spain
  | 'NL' // Netherlands
  | 'DE' // Germany
  | 'IT' // Italy
  | 'NO' // Norway
  | 'SE' // Sweden
  | 'DK' // Denmark
  | 'FI' // Finland
  | 'PL' // Poland
  | 'CZ' // Czech Republic
  | 'HU' // Hungary
  | 'AT' // Austria
  | 'BE' // Belgium
  | 'LU' // Luxembourg
  | 'CH' // Switzerland
  | 'AU' // Australia
  | 'PT'; // Portugal

export type SamplingCampaignRequestCurrency =
  | 'USD'
  | 'EUR'
  | 'CAD'
  | 'GBP'
  | 'AUD'
  | 'CHF'
  | 'CZK'
  | 'DKK'
  | 'HUF'
  | 'NOK'
  | 'PLN'
  | 'SEK';

export interface SamplingCampaignRequestKeyContact {
  name?: string;
  email?: string;
}

export interface SamplingCampaignRequest extends CampaignRequestBase {
  audienceDetails?: AudienceDetails;
  /**
   * The primary client whose product catalog is being used and who will receive
   * the generated content (e.g., Walmart's catalog for a Walmart campaign, or
   * Dove's catalog for a Dove campaign).
   */
  brandBriefUrl?: string;
  bvClientId?: BazaarvoiceClientId;
  campaignRequestId?: SamplingCampaignRequestId;
  comments?: Comments;
  contentExampleImageUrls?: string[];
  contentExampleVideoUrls?: string[];
  created?: Date;
  currency?: SamplingCampaignRequestCurrency;
  displayReviews?: DisplayReviews;
  externalId?: string;
  formApproverEmail?: string;
  formOwner?: string;
  formOwnerEmail?: string;
  guidance?: CampaignRequestGuidanceItem[];
  keyContact?: SamplingCampaignRequestKeyContact;
  fulfillmentOptions?: FulfillmentDetails;
  /**
   * Indicates the purchase workflow type for this campaign.
   * - undefined: Legacy campaign request that needs purchase option revalidation (triggers modal)
   * - true: "Get a Quote" workflow (new purchase to be created)
   * - false: Prepaid workflow (existing purchase option selected)
   */
  isNewPurchase?: boolean;
  lastUpdated?: Date;
  locale?: SamplingCampaignRequestLocale;
  memberTasks?: MemberTasks;
  name?: string;
  obsolete?: boolean;
  products?: BVSamplingProduct[];
  purchaseOption?: PurchaseOption[];
  /**
   * The client who initiated the campaign request. In retailer partnerships,
   * this would be the brand requesting to run a campaign targeting the
   * retailer's customers (e.g., Dove requesting a campaign for Walmart).
   */
  requestorClientId?: BazaarvoiceClientId;
  salesforceNotificationDate?: Date;
  quoteId?: QuoteId;
  externalQuoteId?: ExternalQuoteId;
  samplingProgram?: string;
  status?: SamplingCampaignRequestStatus;
  /**
   * When a campaign targets a different client than the requestor, this
   * specifies the target client. Used in retailer scenarios where a brand
   * creates a campaign for a retailer's audience (e.g., Walmart as target).
   * It should be explicitly set to an empty string to remove the retailer association.
   */
  targetClientId?: BazaarvoiceClientId | '';
  targetPublishDate?: Date;
  type?: 'sampling';
}

export interface EditSamplingCampaignRequestPayload {
  campaign: EditSamplingCampaignRequest;
  id: string;
}

export interface DeleteSamplingCampaignRequestPayload {
  id: string;
}

export interface EditSamplingCampaignRequest {
  audienceDetails?: AudienceDetails;
  brandBriefUrl?: string;
  comments?: Comments;
  contentExampleImageUrls?: string[];
  contentExampleVideoUrls?: string[];
  currency?: SamplingCampaignRequestCurrency;
  displayReviews?: DisplayReviews;
  formOwner?: string;
  formOwnerEmail?: string;
  fulfillmentOptions?: FulfillmentDetails;
  guidance?: CampaignRequestGuidanceItem[];
  isNewPurchase?: boolean;
  keyContact?: SamplingCampaignRequestKeyContact;
  locale?: SamplingCampaignRequestLocale;
  memberTasks?: MemberTasks;
  products?: BVSamplingProduct[];
  purchaseOption?: PurchaseOption[];
  quoteId?: QuoteId;
  externalQuoteId?: ExternalQuoteId;
  status?: SamplingCampaignRequestStatus;
  targetClientId?: BazaarvoiceClientId | '';
  targetPublishDate?: Date;
}

export interface CreateSamplingCampaignRequestResponse {
  data: SamplingCampaignRequest;
}

export interface EditSamplingCampaignRequestResponse {
  data: SamplingCampaignRequest;
}

export interface TransformBulkUploadProductsResponse {
  data: Serialized<BVSamplingProduct>[];
}

export enum TransformBulkUploadProductsValidationContext {
  BV_SHIPPED = 'bv_shipped',
  CLIENT_SHIPPED = 'client_shipped',
}

export interface TransformBulkUploadProductsRequestPayload {
  file: File;
}

// The system fields a column of an uploaded products file can be mapped to
export const BulkUploadProductsField = {
  AvailabilityDate: 'availabilityDate',
  Brand: 'brand',
  Category: 'category',
  Depth: 'depth',
  Description: 'description',
  DimensionUnit: 'dimensionUnit',
  Fmv: 'fmv',
  Height: 'height',
  ImageUrl: 'imageUrl',
  Name: 'name',
  PdpUrl: 'pdpUrl',
  ProductId: 'productId',
  Quantity: 'quantity',
  Upcs: 'upcs',
  VariantEanUpc: 'variantEanUpc',
  VariantImageUrl: 'variantImageUrl',
  VariantName: 'variantName',
  Width: 'width',
} as const;

export type BulkUploadProductsField =
  (typeof BulkUploadProductsField)[keyof typeof BulkUploadProductsField];

// Maps each system field to the header of the file column that provides its value
export type BulkUploadProductsMapping = Partial<
  Record<BulkUploadProductsField, string | null>
>;

export interface BulkUploadProductsParseResult {
  headers: string[];
  suggestedMapping: BulkUploadProductsMapping;
  // Rows of the file, keyed by column header
  sampleRows: Record<string, string>[];
}

export interface ParseBulkUploadProductsResponse {
  data: BulkUploadProductsParseResult;
}

export interface UploadCampaignRequestFilePayload {
  campaignRequestId: SamplingCampaignRequestId;
  file: File;
}

export interface UploadCampaignRequestFileResponse<TData> {
  data: TData;
}

export interface UploadedAssetUrl {
  url: string;
}

export type UploadStatus = 'failed' | 'processing' | 'ready';

/**
 * Content-brief video uploads are asynchronous: the upload responds 202 with
 * a `videoId`, then the caller polls the matching `.../status` route until it
 * reports `ready` with a url (or `failed`). Brand-brief document uploads are
 * synchronous and do not use this shape.
 */
export interface UploadStatusResponse {
  status: UploadStatus;
  url: string | null;
}

export interface StartInspirationVideoUploadResponse {
  videoId: string;
}

export interface TransformBulkUploadProductsQueryParams {
  validationContext?: TransformBulkUploadProductsValidationContext;
}

export enum SamplingCampaignRequestQuoteStatus {
  NEEDS_INITIALISED = 'NEEDS_INITIALISED',
  INITIALISING = 'INITIALISING',
  INITIALISED = 'INITIALISED',
  QUOTING = 'QUOTING',
  QUOTED = 'QUOTED',
  NOT_QUOTABLE = 'NOT_QUOTABLE',
  EXPIRED = 'EXPIRED',
  ERROR_INITIALISING = 'ERROR_INITIALISING',
  ERROR_QUOTING = 'ERROR_QUOTING',
  CONTACT_REQUESTED = 'CONTACT_REQUESTED',
}

export const QUOTE_LOADING_STATUSES: SamplingCampaignRequestQuoteStatus[] = [
  SamplingCampaignRequestQuoteStatus.NEEDS_INITIALISED,
  SamplingCampaignRequestQuoteStatus.INITIALISING,
  SamplingCampaignRequestQuoteStatus.INITIALISED,
  SamplingCampaignRequestQuoteStatus.QUOTING,
];

export interface SamplingCampaignRequestQuote {
  campaignRequestId: SamplingCampaignRequestId;
  quoteId: QuoteId;
  externalQuoteId: ExternalQuoteId;
  opportunityId: string;
  quoteAmount?: number;
  currency?: SamplingCampaignRequestCurrency;
  status?: SamplingCampaignRequestQuoteStatus;
  isValid?: boolean;
  aboveQuotingThreshold?: boolean;
}

export interface GetSamplingCampaignRequestQuoteRequest {
  campaignRequestId?: SamplingCampaignRequestId;
}

export interface RequestCampaignRequestQuotePayload {
  quoteContact: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

export interface RequestCampaignRequestQuoteRequest {
  campaignRequestId: SamplingCampaignRequestId;
  payload: RequestCampaignRequestQuotePayload;
}

export interface GetSamplingCampaignRequestQuoteResponse {
  data: SamplingCampaignRequestQuote;
}
